/**
 * Image pipeline: client validates and converts HEIC→JPEG (worker, main-thread fallback),
 * then uploads. Backend handles resizing, WebP conversion, and compression.
 * iOS sends HEIC; Android usually sends JPEG.
 */

import { logger } from '@/lib/logger'
import { convertHeicViaWorker } from '@/helpers/heicWorker'

export const MAX_FILE_SIZE = 30 * 1024 * 1024 // 30MB — matches API limit
export const MAX_FILES = 15
export const UPLOAD_CHUNK_SIZE = 5 // smaller batches reduce memory pressure and improve resilience

export const ACCEPTED_IMAGE_FORMATS = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'image/tiff': ['.tiff', '.tif'],
  'image/avif': ['.avif'],
}

export const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/tiff',
  'image/avif',
]

function needsHeicConversion(file: File): boolean {
  return ['image/heic', 'image/heif'].includes(file.type.toLowerCase())
}

/** Main-thread HEIC→JPEG fallback — blocks UI but keeps upload working when the worker is unavailable. */
async function convertHeicToJpegFallback(file: File): Promise<File> {
  try {
    // @ts-expect-error - heic-convert has no types
    const { default: convert } = await import('heic-convert')
    const arrayBuffer = await file.arrayBuffer()
    const outputBuffer = await convert({
      buffer: Buffer.from(arrayBuffer),
      format: 'JPEG',
      quality: 1,
    })
    const blob = new Blob([outputBuffer], { type: 'image/jpeg' })
    const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg')
    return new File([blob], fileName, { type: 'image/jpeg' })
  } catch (error) {
    logger.error('HEIC conversion failed (fallback):', error)
    throw new Error(
      'Failed to convert HEIC/HEIF image. Please try another format.'
    )
  }
}

export async function createImagePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/** HEIC/HEIF: tries worker conversion first, falls back to main thread. All other formats pass through as-is. */
export async function processFileForUpload(file: File): Promise<File> {
  if (!needsHeicConversion(file)) return file
  logger.log('🔄 Converting HEIC/HEIF to JPEG (worker)…')
  try {
    const converted = await convertHeicViaWorker(file)
    logger.log(
      '✅ Converted to JPEG (worker):',
      converted.name,
      `(${(converted.size / 1024 / 1024).toFixed(2)}MB)`
    )
    return converted
  } catch (workerErr) {
    logger.warn(
      '⚠️ Worker conversion failed — falling back to main thread:',
      workerErr
    )
    const converted = await convertHeicToJpegFallback(file)
    logger.log(
      '✅ Converted to JPEG (fallback):',
      converted.name,
      `(${(converted.size / 1024 / 1024).toFixed(2)}MB)`
    )
    return converted
  }
}

export interface ProcessFilesResult {
  processed: File[]
  failed: Array<{ filename: string; error: string }>
}

export async function processFilesForUpload(
  files: File[]
): Promise<ProcessFilesResult> {
  const processed: File[] = []
  const failed: Array<{ filename: string; error: string }> = []
  for (const file of files) {
    try {
      const result = await processFileForUpload(file)
      processed.push(result)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      logger.error(`Failed to process ${file.name}:`, error)
      failed.push({ filename: file.name, error: message })
    }
  }
  return { processed, failed }
}

export async function processImageFile(
  file: File
): Promise<{ file: File; preview: string }> {
  try {
    const processedFile = await processFileForUpload(file)
    const preview = await createImagePreview(processedFile)
    return { file: processedFile, preview }
  } catch (error) {
    logger.error('Failed to process image:', error)
    throw error
  }
}

export function validateImageFile(
  file: File,
  maxSize: number = MAX_FILE_SIZE
): { valid: boolean; error?: string } {
  if (!ALLOWED_MIME_TYPES.includes(file.type.toLowerCase())) {
    return {
      valid: false,
      error:
        'Please upload a valid image file (JPEG, PNG, GIF, WebP, HEIC, TIFF, or AVIF)',
    }
  }
  if (file.size > maxSize) {
    const maxSizeMB = Math.floor(maxSize / 1024 / 1024)
    return { valid: false, error: `File size must be less than ${maxSizeMB}MB` }
  }
  return { valid: true }
}

export function validateMultipleImageFiles(
  files: File[],
  maxFiles: number = MAX_FILES,
  maxSize: number = MAX_FILE_SIZE
): { valid: boolean; error?: string } {
  if (files.length > maxFiles) {
    return {
      valid: false,
      error: `You can only upload up to ${maxFiles} images`,
    }
  }
  for (const file of files) {
    const result = validateImageFile(file, maxSize)
    if (!result.valid) return result
  }
  return { valid: true }
}

export function generateRandomId(): string {
  return Math.random().toString(36).substring(2, 15)
}

export function generateStoragePath(
  userId: string,
  invitationId: string,
  folder: string,
  randomId?: string
): string {
  const id = randomId || generateRandomId()
  return `${userId}/${invitationId}/${folder}/${id}`
}
